import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { Patient } from "../model/patient.";
import { ActivatedRoute, Router } from "@angular/router";
import { throwError, Observable, Subject } from "rxjs";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { FhirService } from "./fhir.service";
import { AppConfig } from "../shared/constants/app.config";
import { take, retry } from "rxjs/operators";
import { DialogComponent } from "../dialog/dialog.component";
import { HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { UtilityService } from "../shared/service/utility.service";
import { SpinnerService } from "../spinner/spinner.service";
import { Validator } from "../shared/constants/validator_regex";
import { AngularAppLogger } from "../angular-app-logger";
import { CibmtrPatientService } from "../patient/cibmtr-patient.service";
import { OrganizationService } from "../shared/service/organization.service";
import {AppStateService} from "../shared/service/app.state.service";

@Component({
    selector: "app-main",
    templateUrl: "./patient.component.html",
    styleUrls: ["./patient.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PatientComponent implements OnInit {
  dialogRef: MatDialogRef<DialogComponent>;
  ehrpatient: Patient;
  cibmtrPatientCount: any;
  crid: string;
  logicalId: string;
  isCibmtrCallSuccess = false;
  ccn: string;
  isLoading: boolean;
  now: Date;
  dataManagerName: string;
  selectedCenterName: string;
  cridSubject: Subject<any> = new Subject();
  crid$: Observable<string> = this.cridSubject.asObservable();
  isValidSsn: boolean;
  ssn: string;
  nonPIIIdentifiers: any[];
  ssnIdentifier: any;
  hasinvalidraceCodes:boolean;
  selectedOrg: any;
  filteredPatientName: any;
  userId: string;
  patientName: string;
  hasPatientDataError = false;
  receivedIss: string | null = null;
  receivedPatientId: string | null = null;

  constructor(
    readonly route: ActivatedRoute,
    readonly dialog: MatDialog,
    readonly fhirService: FhirService,
    readonly router: Router,
    readonly spinner: SpinnerService,
    readonly utility: UtilityService,
    readonly ssnregex: Validator,
    readonly appLogger: AngularAppLogger,
    readonly patientService: CibmtrPatientService,
    readonly organizationService: OrganizationService,
    readonly appStateService: AppStateService,
  ) {}

  ngOnInit() {

    console.log("in patient.component");
    // Check if patient data is available
    this.appStateService.patient$.pipe(take(1)).subscribe(patientId => {
      const issUrl = localStorage.getItem('iss');

      // Store received values for display
      this.receivedIss = issUrl;
      this.receivedPatientId = patientId;

      if (!patientId || !issUrl) {
        this.hasPatientDataError = true;
        const errorMessage = `Patient data not available. ISS: ${issUrl || 'NotProvided'}, Patient ID: ${patientId || 'NotProvided'}. Please ensure proper EHR launch.`;
        this.appLogger.logError(errorMessage, {
          context: 'PatientComponent.ngOnInit - Missing EHR data',
          url: issUrl,
          allowDuplicate: false
        });
        return;
      }
    });

    this.determineModal()
      .then((cibmtrCenters) => {
        if (cibmtrCenters?.length > 1) {
          this.dialogRef = this.dialog.open(DialogComponent, {
            data: { cibmtrCenters },
            disableClose: true,
          });
          this.dialogRef.afterClosed().subscribe((selectedItem) => {
            if (selectedItem) {
              this.subscribeRouteData(selectedItem);
              this.appLogger.setTcNum(this.userId, selectedItem.value);
            }
          });
        } else if (cibmtrCenters) {
          this.subscribeRouteData(cibmtrCenters[0]);
        }
        this.appLogger.logInfo(cibmtrCenters, { context: `Fetched all Cibmtr centers in PatientComponent` });
      })
      .catch((error) => {
        this.appLogger.logError(error, { context: `Error in fetching Cibmtr centers in PatientComponent`});
      });
  }

  // Get the decodedJWT token from the LandingPageComponent via routed history.state
  async determineModal(): Promise<any[]> {
    const state = history.state;
    const decodedValue = state.decoded;
    let scopes = decodedValue.authz_cibmtr_fhir_ehr_client?.filter(
      (item) => item.includes("_role_rc") && item.includes("_fn3")
    );

    this.dataManagerName = `${decodedValue.first_name} ${decodedValue.last_name}`;
    this.userId = decodedValue.email;

    scopes.forEach((scope, index) => {
      scopes[index] = scope.match(/(rc_\d+)_fn3/)[1];
    });
    scopes = scopes.join(",");
    return this.organizationService.fetchCenters(scopes);
  }

  // Updated this to work for when name[0].text is not available, but can use "given" and "family" instead
  name(ehrpatient) {
    const name = ehrpatient.name.filter(
      (e) => e.use === "official" || e.use === "usual"
    );
    if (!name || name.length === 0) {
      this.appLogger.logError("Patient name does not contain official or usual use tags", { context: "Error in name of patient" });
      this.appLogger.logInfo("Name array in Patient - " + JSON.stringify(ehrpatient.name), { context: "name of ehrPatient" });
      throw new Error("Error in Patient data. Name does not contain official or usual use info");
    }
    this.filteredPatientName = name[0];
    return this.filteredPatientName.text ? this.filteredPatientName.text : this.filteredPatientName.given[0] + " " + this.filteredPatientName.family;
  }

  // resolver now only expects and returns 1 ehrpatient resource - hence no need for the array
  subscribeRouteData = (selectedOrg) => {
      this.spinner.end();
      this.route.data.subscribe(
        (results) => {
          // Check if patient data is null (from resolver)
          if (!results.pageData) {
            this.hasPatientDataError = true;
            const patientId = this.receivedPatientId;
            const issUrl = localStorage.getItem('iss');
            const errorMessage = `Patient data could not be loaded. ISS: ${issUrl || 'missing'}, Patient ID: ${patientId || 'missing'}. Please check your EHR connection.`;
            this.appLogger.logError(errorMessage, {
              context: 'PatientComponent.subscribeRouteData - No resolver data',
              url: issUrl,
              allowDuplicate: false
            });
            return;
          }

          this.ehrpatient = results.pageData;
          this.selectedOrg = selectedOrg;
          this.patientName = this.name(this.ehrpatient);
          this.validate(this.ehrpatient);
          this.retreiveFhirPatient(this.ehrpatient, selectedOrg);
          this.updateEhrPatientReference(this.ehrpatient);
          this.appLogger.logInfo(this.ehrpatient, { context: "Obtained Ehr Patient from route subscription" });
        },
        (error) => {
          this.spinner.reset();
          this.appLogger.logError(error, { context: "Error in route subscription when retrieving Ehr Patient" });
          return throwError(error);
        }
    );
  };

  //TODO : Refactor for R3 if necessary. Logica no longer needed
  updateEhrPatientReference(ehrPatient) {
    if (
      localStorage.getItem("fhir_version")?.includes("R4")
    ) {
      this.utility.replaceReference(ehrPatient);
    }
  }

  //Intial search Patient in FHIR server for CRID Lookup
  retreiveFhirPatient(ehrpatient, selectedOrg) {
    localStorage.removeItem("patient_local_id");
    this.ccn = selectedOrg.value;
    this.selectedCenterName = selectedOrg.name;
    const ehrPatientId = `/Patient/${ehrpatient.id}`;
    const rebuiltIssUrl = this.utility.rebuild_DSTU2_STU3_Url(localStorage.getItem("iss"));
    const logicalId = encodeURI(
      "".concat(AppConfig.FHIR_LOGICALID_NAMESPACE, "|", rebuiltIssUrl, ehrPatientId)
    );
    const encodedScope = encodeURI(
      "".concat(AppConfig.CIBMTR_CENTERS_NAMESPACE, "|", this.ccn)
    );

    this.fhirService
      .lookupPatientIdentifier(logicalId.concat(`&_security=${encodedScope}`))
      .pipe(take(1))
      .subscribe(
        (resp: any) => {
          this.cibmtrPatientCount = resp.total;
          if (this.cibmtrPatientCount > 0) {
            this.isCibmtrCallSuccess = true;
            if (this.cibmtrPatientCount > 1) {
              this.appLogger.logError(`Duplicate patient resource found in CIBMTR. Please contact CIBMTR to resolve`, {context: `Duplicate patient found in cibmtr. Please resolve.`});
            }
            if (resp.entry) {
              resp.entry.filter((entry) => {
                if (entry.resource) {
                  localStorage.setItem(
                    "patient_local_id",
                    entry.resource.id
                  );
                  if (
                    entry.resource.identifier &&
                    entry.resource.identifier.length > 0
                  ) {
                    const filteredCrid = entry.resource.identifier.filter(
                      (i) => i.system === AppConfig.CIBMTR_CRID_NAMESPACE
                    );
                    if (filteredCrid && filteredCrid.length > 0) {
                      this.crid = filteredCrid[0].value;
                    }
                  }
                }
              });
            }
          } else {
            if (localStorage.getItem("fhir_version")?.includes("R4")) {
              this.fhirService.lookupPatientIdentifierSTU3(logicalId.concat(`&_security=${encodedScope}`))
                .pipe(take(1))
                .subscribe(
                  (fhirServerResponse: any) => {
                    this.cibmtrPatientCount = fhirServerResponse.total;
                    if (this.cibmtrPatientCount > 0) {
                      this.isCibmtrCallSuccess = true;
                      if (fhirServerResponse.entry) {
                        fhirServerResponse.entry.filter((entry) => {
                          if (entry.resource) {
                            localStorage.setItem(
                              "patient_local_id",
                              entry.resource.id
                            );
                            if (
                              entry.resource.identifier &&
                              entry.resource.identifier.length > 0
                            ) {
                              const filteredCrid = entry.resource.identifier.filter(
                                (i) => i.system === AppConfig.CIBMTR_CRID_NAMESPACE
                              );
                              if (filteredCrid && filteredCrid.length > 0) {
                                this.crid = filteredCrid[0].value;
                              }
                            }
                          }
                        });
                      }
                      this.cridSubject.next("Patient lookup Successful");
                      this.regPatient(ehrpatient);
                    }
                  },
                  (error) => {
                    this.handleError(error);
                  }
                );
            }
          }
          this.cridSubject.next("Patient lookup Successful");
        },
        (error) => {
          this.handleError(error);
        }
      );
  }

  //Associate CRID
  register(e: any, ehrpatient: any) {
    e.preventDefault();
    e.stopPropagation();
    this.regPatient(ehrpatient);
  }

  regPatient(ehrpatient: any) {

    this.isLoading = true;

    //CRID Payload
    let payload = this.mapCridPayLoad(ehrpatient);

    const removeEmptyOrNull = (obj: any): any => {
      if (Array.isArray(obj)) {
        return obj.filter(item => {
          if (typeof item === "object" && item !== null) {
            const cleaned = removeEmptyOrNull(item);
            return cleaned && Object.keys(cleaned).length > 0;
          }
          return item !== "" && item !== null && item !== undefined;
        });
      }

      if (typeof obj === "object" && obj !== null) {
        Object.keys(obj).forEach((key) => {
          if (obj[key] && typeof obj[key] === "object") {
            obj[key] = removeEmptyOrNull(obj[key]);
          }
          if (obj[key] === "" || obj[key] === null || obj[key] === undefined ||
              (Array.isArray(obj[key]) && obj[key].length === 0)) {
            delete obj[key];
          }
        });
        return Object.keys(obj).length > 0 ? obj : undefined;
      }

      return obj;
    };

    payload = removeEmptyOrNull(payload);

    this.fhirService
      .getCrid(payload)
      .pipe(take(1))
      .subscribe(
        (result) => {
          this.isLoading = false;

          try {
            const cridValue = result?.parameter?.[0]?.identifier?.[0]?.value;
            if (!cridValue) {
              throw new Error('Invalid CRID response: missing identifier value');
            }
            this.crid = cridValue;
          } catch (error) {
            this.appLogger.logError(error, {component: "PatientComponent", context: "Error in getCrid"});
            this.handleError(error as HttpErrorResponse);
            return;
          }

          //get EHR logical id
          this.logicalId = ehrpatient.id;

          const createEhrPatient = this.patientService.appendCridIdentifier(
            ehrpatient,
            this.crid,
            this.logicalId,
            this.ccn
          );

          //Lookup CRID in FHIR Server
          const cridSearchurl = encodeURI(
            "".concat(AppConfig.CIBMTR_CRID_NAMESPACE, "|", this.crid)
          );

          this.fhirService
            .lookupPatientIdentifier(
              cridSearchurl.concat(`&_security=${this.ccn}`)
            )
            .subscribe((cibmtrPatient): void => {
              this.cibmtrPatientCount = cibmtrPatient.total;
              if (this.cibmtrPatientCount > 0) {
                this.fhirService
                  .updatePatient(
                    this.patientService.mergedPatient(
                      ehrpatient,
                      cibmtrPatient.entry[0].resource
                    ),
                    cibmtrPatient.entry[0]?.resource?.id
                  )
                  .pipe(retry(1))
                  .subscribe(
                    (response: HttpResponse<any>) => {
                      this.isCibmtrCallSuccess = true;
                      this.setPatientLocalId(response.headers.get("Location") ?? '');
                    },
                    (error) => {
                      this.handleError(error);
                    }
                  );
              } else {
                //Patient record not found create the entry
                this.fhirService
                  .submitPatient(createEhrPatient)
                  .pipe(retry(1))
                  .subscribe(
                    (response: HttpResponse<any>) => {
                      this.isCibmtrCallSuccess = true;
                      this.appLogger.logInfo("Submitted patient", { context: "Patient registered successfully" });
                      this.setPatientLocalId(response.headers.get("Location") ?? '');
                    },
                    (error) => {
                      this.handleError(error);
                    }
                  );
                if (result) {
                  this.appLogger.logInfo(result, { context: `Crid retrieved / assigned successfully` });
                }
              }
            },
            (error) => {
              this.handleError(error);
            }
            );
        },
        (error) => {
          this.appLogger.logError(error, { context: `Error in patient crid assignment / registration` });
          this.handleError(error);
        }
      );
  }

  mapCridPayLoad(ehrpatient: any) {
    const validatedPayload = this.validatedCridPayload(ehrpatient);

    return {
      resourceType: "Parameters",
      parameter: [
        {
          name: "resource",
          resource: {
            ...validatedPayload,
            meta: {
              security: [{
                system: AppConfig.CIBMTR_CENTERS_NAMESPACE,
                code: this.ccn
              }]
            },
          }
        },
        {
          name: "ccn",
          valueString: this.ccn.substring(3)
        }
      ]
    };
  }

  validatedCridPayload(ehrpatient: any) {
    if (!ehrpatient) {
      throw new Error('Patient data is required');
    }

    let cleaned = this.hasinvalidraceCodes
      ? this.patientService.removeInvalidRaceCodes(ehrpatient)
      : ehrpatient;

    if (!cleaned) {
      throw new Error('Patient data is invalid after cleaning');
    }

    return {
      ...cleaned,
      identifier: !this.isValidSsn
        ? cleaned?.identifier?.filter(i => !AppConfig.SSN_SYSTEM.includes(i.system))
        : cleaned.identifier
    };
  }

  setPatientLocalId(locationUrl: string) {
    localStorage.setItem(
      "patient_local_id",
      locationUrl.substring(locationUrl.indexOf("Patient")).split("/")[1]
    );
    this.appLogger.logInfo("Set the local patientId. Patient local id - " + localStorage.getItem("patient_local_id"),
      {component: "PatientComponent", url: locationUrl, context: "setPatientLocalId"});
  }

  validate(ehrpatient: any) {
   //UI Validations - Patient
    this.nonPIIIdentifiers = ehrpatient.identifier?.filter(
      (i) => !AppConfig.SSN_SYSTEM.includes(i.system)
    ) || [];

    this.ssnIdentifier = ehrpatient.identifier?.filter((i) =>
      AppConfig.SSN_SYSTEM.includes(i.system)
    ) || [];

    if (this.ssnIdentifier?.length > 0) {
      this.ssn = this.ssnIdentifier[0].value;
      if (this.ssnregex.validateSSN(this.ssn)) {
        this.isValidSsn = true;
      }
    }

    this.hasinvalidraceCodes = this.patientService.getRaceCodes(ehrpatient).hasInvalid;

    // Set filteredPatientName before validation
    const name = ehrpatient.name.filter(
      (e) => e.use === "official" || e.use === "usual"
    );
    if (!name || name.length === 0) {
      throw new Error("Patient name does not contain official or usual use tags");
    }
    this.filteredPatientName = name[0];
  }

  proceed() {
    this.utility.data = {
      //Stringified JSON object for IE11 issue
      ehrpatient: JSON.stringify(this.ehrpatient),
      crid: this.crid,
      selectedOrg: this.selectedOrg,
    };
    this.router
      .navigate(["/patientdetail"])
      .then((e) => {
        this.appLogger.logInfo(`Navigated to Patient Detail page. Crid - ${this.crid}` , { context: `Success in proceeding to PatientDetail from PatientComponent` });
      })
      .catch((error) => {
        this.appLogger.logError(error, { context: `Error in navigating to patientdetail page` });
      });
  }

  handleError(error: HttpErrorResponse) {
    const date = new Date();
    this.isLoading = false;
    const errorMessage = `An unexpected failure has occurred. Please try again. If the error persists, please report this to CIBMTR. Status: ${error.status
      } \n Message : ${error.error?.errorMessage || error.message}.`;
    this.appLogger.logError(errorMessage, {context: "Error occured in Patient Component", url: error.url ?? undefined, crid: this.crid});
    this.appLogger.logError(error, {context: "Logging from the handleError method in the Patient Component"});
    return throwError(error);
  }
}
